import { useCallback, useEffect, useMemo } from 'react';

import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useGetPseDay7MatingsQuery } from '@/app/services/excel';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useSites } from '@/services/sites';
import { formatDateWithPattern } from '@/utils';

import { MatingStats } from './types';

type Props = {
  className?: string;
  setMatingStats: (matingStats: MatingStats) => void;
};

export const PseDay7Selector = ({ className, setMatingStats }: Props) => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const beginDate = params.get('beginDate');
  const endDate = params.get('endDate');
  const siteId = params.get('site');

  const fertilizationId = params.get('fertId');
  const donorName = params.get('donorName');

  const { data: siteData } = useSites({ filter: 'fertilization' });
  const { data: pseDay7MatingsData } = useGetPseDay7MatingsQuery(
    {
      beginDate,
      endDate,
      siteId,
    },
    { skip: !beginDate || !endDate || !siteId },
  );

  const matingOptions = useMemo(
    () =>
      pseDay7MatingsData?.map((item) => ({
        label: item.mating,
        value: item.id,
      })) || [],
    [pseDay7MatingsData],
  );

  const sites = siteData?.map((item) => ({ label: item.name, value: item.id }));

  useEffect(() => {
    if (pseDay7MatingsData) {
      const selectedMating = pseDay7MatingsData?.find(
        (mating) => mating.id === fertilizationId,
      );
      if (selectedMating) {
        setMatingStats({
          trf: selectedMating.transferred || 0,
          frz: selectedMating.frozen || 0,
          g1: selectedMating.grade1 || 0,
          g2: selectedMating.grade2 || 0,
          g3: selectedMating.grade3 || 0,
          dcg1: selectedMating.discardedGrade1 || 0,
          dcg2: selectedMating.discardedGrade2 || 0,
          dcg3: selectedMating.discarded || 0,
        });
      }
    }
  }, [pseDay7MatingsData, fertilizationId]);

  const handleBeginDateChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set(
          'beginDate',
          formatDateWithPattern(e.value, 'MM-dd-yyyy'),
        );
      } else {
        newParams.delete('beginDate');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleEndDateChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('endDate', formatDateWithPattern(e.value, 'MM-dd-yyyy'));
      } else {
        newParams.delete('endDate');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleSiteChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('site', e.value);
      } else {
        newParams.delete('site');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleFertilizationChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('fertId', e.value);

        const selectedMating = pseDay7MatingsData?.find(
          (mating) => mating.id === e.value,
        );

        newParams.set('donorName', selectedMating?.mating || '');
      } else {
        newParams.delete('fertId');

        setMatingStats({
          trf: 0,
          frz: 0,
          g1: 0,
          g2: 0,
          g3: 0,
          dcg1: 0,
          dcg2: 0,
          dcg3: 0,
        });
      }
      setParams(newParams);
    },
    [params, setParams, pseDay7MatingsData, setMatingStats],
  );

  return (
    <div className={`pse-day7__selector ${className || ''}`.trim()}>
      <Calendar
        value={beginDate ? new Date(beginDate) : null}
        dateFormat="mm-dd-yy"
        onChange={handleBeginDateChange}
        showIcon
        placeholder={t('fertilization.fertDate')}
        hideOnDateTimeSelect
      />
      <Calendar
        value={endDate ? new Date(endDate) : null}
        dateFormat="mm-dd-yy"
        onChange={handleEndDateChange}
        showIcon
        placeholder={t('transfer.transferDate')}
        hideOnDateTimeSelect
      />

      <Dropdown
        value={siteId}
        onChange={handleSiteChange}
        options={sites}
        placeholder={t('site.site')}
        className="site-dropdown"
        filter
        disabled={!beginDate || !endDate}
      />
      <Dropdown
        value={fertilizationId}
        onChange={handleFertilizationChange}
        options={matingOptions}
        placeholder={t('mating.mating')}
        className="site-dropdown"
      />
    </div>
  );
};
